import { CTAGroup, CTAGroupField, CTAGroupProps } from "@affinity/components/CTAGroup";
import { colours } from "@affinity/constants/StyleConstants";
import { breakpointWidth, rem } from "@affinity/constants/StyleHelpers";
import { FLOALA_TOOLBAR_COLLAPSED, FLOALA_TOOLBAR_EXPANDED, FROALA_CONFIG } from "@afgroup/constants/froalaConfig";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { LinkPresentationProps, StyledClickable } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { FC } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    isAuthenticated: state.context.session?.isAuthenticated,
    userName: state.context.session?.isAuthenticated ? state.context.session.firstName : undefined,
});

export const fields = {
    background: "background",
    image: "image",
    focalPoint: "focalPoint",
    backgroundColor: "backgroundColor",
    heading: "heading",
    subheading: "subheading",
    cta: "cta",
    label: "label",
    link: "link",
    variant: "variant",
    icon: "icon",
    iconLocation: "iconLocation",
    bannerWidth: "bannerWidth",
    customCSS: "customCSS",
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.background]: "image" | "color";
        [fields.image]: string;
        [fields.focalPoint]: string;
        [fields.backgroundColor]: string;
        [fields.heading]: string;
        [fields.subheading]: string;
        [fields.label]: string;
        [fields.customCSS]: string;
    } & CTAGroupProps;
    extendedStyles?: BannerStyles;
}

export interface BannerStyles {
    wrapper?: InjectableCss;
    overlayWrapper?: InjectableCss;
    /**
     * @deprecated Use the `bannerButton` property instead.
     */
    slideCenteringWrapperStyles?: InjectableCss;
    bannerLink?: LinkPresentationProps;
    bannerButton?: ButtonPresentationProps;
}

export const bannerStyles: BannerStyles = {
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
        `,
    },
    overlayWrapper: {
        css: css`
            width: 100%;
            color: white;
            text-align: center;
        `,
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export const Banner: FC<Props> = ({ isAuthenticated, userName, fields }) => {
    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <BannerWrapper className="banner-wrapper" {...customCssWrapper} noImage={!fields.image}>
            <TextWrapper>
                {isAuthenticated && parse(fields.heading.replace(/<\/h(\d)>/, `, ${userName}</h$1>`), parserOptions)}
                {!isAuthenticated && parse(fields.heading, parserOptions)}
                {parse(fields.subheading, parserOptions)}
                <ButtonsWrapper>
                    <CTAGroup cta={fields.cta} />
                </ButtonsWrapper>
            </TextWrapper>
            {fields.image && (
                <ImageWrapper>
                    <Image src={fields.image} alt={fields.heading} _focalPoint={fields.focalPoint} />
                </ImageWrapper>
            )}
        </BannerWrapper>
    );
};

const BannerWrapper = styled.div<{ noImage?: boolean }>`
    position: relative;
    z-index: 0;
    display: flex;
    flex-direction: column;
    @media (min-width: ${breakpointWidth("md")}) {
        flex-direction: row;
        padding-bottom: clamp(12px, -6.6667px + 5.8333vw, 68px);
    }
    ::before {
        position: absolute;
        top: 0;
        left: 0;
        margin: 0 calc((100% - 100vw) / 2);
        width: 100vw;
        height: 100%;
        z-index: -1;
        display: block;
        content: "";
        background-color: ${colours.darkBlue};
        ${({ noImage }) =>
            noImage &&
            css`
                background: ${colours.darkBlue} url("/staticContent/hero-bg.svg") no-repeat bottom right / auto 100%;
            `}
    }
    ::after {
        position: absolute;
        bottom: -1px;
        left: 0;
        right: 0;
        margin: 0 calc((100% - 100vw) / 2);
        width: 100vw;
        height: 12px;
        height: clamp(12px, -6.6667px + 5.8333vw, 68px);
        z-index: 1;
        display: block;
        content: "";
        background-color: ${colours.white};
        clip-path: ellipse(70% 320% at 50% 320%);
    }
`;

const TextWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: start;
    color: white;
    gap: ${rem(15)};
    padding: ${rem(30)} 0;
    @media (min-width: ${breakpointWidth("md")}) {
        min-width: 40%;
        flex: 40% 0 0;
        margin-right: -10%;
    }
    @media (min-width: ${breakpointWidth("lg")}) {
        gap: ${rem(25)};
    }
    p {
        margin: 0;
        font-weight: 300;
        font-size: ${rem(18)};
        line-height: 1.66em;
        @media (min-width: ${breakpointWidth("lg")}) {
            font-size: ${rem(24)};
        }
    }
    big {
        font-size: 1.5em;
    }
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        margin-bottom: -0.43em;
        margin-top: -0.07em; // TODO: make it reusable
    }
    ${StyledClickable} {
        font-size: ${rem(18)};
        color: ${colours.white};
        text-decoration: underline;
        display: inline-flex;
        gap: ${rem(8)};
        @media (min-width: ${breakpointWidth("lg")}) {
            font-size: ${rem(20)};
            svg {
                width: ${rem(24)};
                height: ${rem(24)};
                flex-shrink: 0;
            }
        }
        :hover {
            color: ${colours.lightBlue};
            text-decoration: underline;
        }
        span {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
        }
    }
`;

const ButtonsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${rem(15)};
    @media (min-width: ${breakpointWidth("lg")}) {
        flex-direction: row;
        flex-wrap: wrap;
        gap: ${rem(20)};
    }
`;

const ImageWrapper = styled.div`
    aspect-ratio: 5 / 3;
    margin-right: calc((100% - 100vw) / 2);
    margin-left: calc((100% - 100vw) / 2);
    clip-path: ellipse(70% 60% at 50% 60%);
    @media (min-width: ${breakpointWidth("md")}) {
        aspect-ratio: 2 / 1;
        margin-top: ${rem(20)};
        margin-bottom: clamp(-68px, 6.6667px - 5.8333vw, -12px);
        margin-left: 0;
        clip-path: ellipse(80% 150% at 80% 150%);
    }
    @media (min-width: ${breakpointWidth("lg")}) {
        margin-top: ${rem(40)};
    }
`;

export type ImageProps = {
    _focalPoint?: string;
};

const Image = styled.img<ImageProps>`
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: ${({ _focalPoint }) => _focalPoint};
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const banner: WidgetModule = {
    component: connect(mapStateToProps)(Banner),
    definition: {
        group: "Basic",
        icon: "image-landscape",
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.image,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.focalPoint,
                displayName: "Focal Point",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top Left", value: "top left" },
                    { displayName: "Top Center", value: "top center" },
                    { displayName: "Top Right", value: "top right" },
                    { displayName: "Center Left", value: "center left" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Center Right", value: "center right" },
                    { displayName: "Bottom Left", value: "bottom left" },
                    { displayName: "Bottom Center", value: "bottom center" },
                    { displayName: "Bottom Right", value: "bottom right" },
                ],
                defaultValue: "center",
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.heading,
                editorTemplate: "RichTextField",
                displayName: "Heading",
                defaultValue: "",
                extendedConfig: {
                    height: 100,
                    ...FROALA_CONFIG,
                    paragraphFormat: {
                        N: "Normal",
                        H1: "Heading 1",
                        H2: "Heading 2",
                        H3: "Heading 3",
                        H4: "Heading 4",
                    },
                },
                collapsedToolbarButtons: FLOALA_TOOLBAR_COLLAPSED,
                expandedToolbarButtons: FLOALA_TOOLBAR_EXPANDED,
                tab: basicTab,
            },
            {
                fieldType: "Translatable",
                name: fields.subheading,
                editorTemplate: "RichTextField",
                displayName: "Subheading",
                defaultValue: "",
                extendedConfig: {
                    height: 170,
                    ...FROALA_CONFIG,
                },
                collapsedToolbarButtons: FLOALA_TOOLBAR_COLLAPSED,
                expandedToolbarButtons: FLOALA_TOOLBAR_EXPANDED,
                tab: basicTab,
            },
            CTAGroupField,
        ],
    },
};

export default banner;
